/**
 * Core Mafia Game Types
 *
 * This file defines all TypeScript types and interfaces for the Mafia (The Village)
 * social deduction game. It follows the design specified in
 * .kiro/specs/mafia-game/design.md
 *
 * Mafia is online multiplayer only — no AI, no single-player, no local multiplayer.
 * The game supports 5–15 players in Standard Mode and 16–30 in Extended Mode.
 *
 * Requirements: 2.5, 3.1, 3.2, 14.1, 30.1
 */

// ============================================================================
// Role and Team Types
// ============================================================================

/** The four core Mafia roles */
export type MafiaRole = 'villager' | 'mafia' | 'doctor' | 'detective';

/** Team affiliation: village or mafia */
export type Team = 'village' | 'mafia';

/** Maps each role to its team */
export const ROLE_TEAM_MAP: Record<MafiaRole, Team> = {
  villager: 'village',
  mafia: 'mafia',
  doctor: 'village',
  detective: 'village',
};

/** Descriptive information about a role */
export interface RoleInfo {
  role: MafiaRole;
  team: Team;
  name: string;
  description: string;
  icon: string;
}

/** Role metadata for all four roles */
export const ROLE_INFO: Record<MafiaRole, RoleInfo> = {
  villager: {
    role: 'villager',
    team: 'village',
    name: 'Villager',
    description: 'No special ability. Use discussion and deduction to find the Mafia.',
    icon: '🏘️',
  },
  mafia: {
    role: 'mafia',
    team: 'mafia',
    name: 'Mafia',
    description: 'Knows other Mafia members. Votes to eliminate one Villager each Night.',
    icon: '🔪',
  },
  doctor: {
    role: 'doctor',
    team: 'village',
    name: 'Doctor',
    description:
      'Protects one player from elimination each Night. Cannot protect the same player two Nights in a row.',
    icon: '💊',
  },
  detective: {
    role: 'detective',
    team: 'village',
    name: 'Detective',
    description:
      'Investigates one player each Night to learn if they are Mafia or not.',
    icon: '🔍',
  },
};

// ============================================================================
// Role Distribution
// ============================================================================

/** Role counts for a given player count bracket */
export interface RoleDistribution {
  mafia: number;
  doctor: number;
  detective: number;
  villager: number;
}

/**
 * Standard Mode distribution (5–15 players).
 * Villager count is computed: playerCount - mafia - doctor - detective.
 */
export const STANDARD_DISTRIBUTION: Record<string, Omit<RoleDistribution, 'villager'>> = {
  '5-6':   { mafia: 1, doctor: 1, detective: 1 },
  '7-9':   { mafia: 2, doctor: 1, detective: 1 },
  '10-12': { mafia: 3, doctor: 1, detective: 1 },
  '13-15': { mafia: 4, doctor: 1, detective: 1 },
};

/**
 * Extended Mode distribution (16–30 players).
 * Villager count is computed: playerCount - mafia - doctor - detective.
 */
export const EXTENDED_DISTRIBUTION: Record<string, Omit<RoleDistribution, 'villager'>> = {
  '16-18': { mafia: 5, doctor: 2, detective: 2 },
  '19-21': { mafia: 6, doctor: 2, detective: 2 },
  '22-24': { mafia: 7, doctor: 2, detective: 3 },
  '25-27': { mafia: 8, doctor: 3, detective: 3 },
  '28-30': { mafia: 9, doctor: 3, detective: 3 },
};

// ============================================================================
// Player Types
// ============================================================================

/** Whether a player is alive or dead */
export type PlayerStatus = 'alive' | 'dead';

/** Player connection state */
export type ConnectionStatus = 'connected' | 'disconnected' | 'reconnecting';

/** Full player state (server-side includes role/team; clients see null for others) */
export interface MafiaPlayer {
  id: string;
  name: string;
  role: MafiaRole;
  team: Team;
  status: PlayerStatus;
  connectionStatus: ConnectionStatus;
  isHost: boolean;
  disconnectedPhases: number;
}

/** Player info shown in the lobby before the game starts */
export interface LobbyPlayer {
  id: string;
  name: string;
  isHost: boolean;
  isReady: boolean;
  connectionStatus: ConnectionStatus;
}

// ============================================================================
// Game Phase Types
// ============================================================================

/** Discriminated union for game phases */
export type GamePhase =
  | { type: 'lobby' }
  | { type: 'roleReveal' }
  | { type: 'night'; nightNumber: number }
  | { type: 'dayDiscussion'; dayNumber: number }
  | { type: 'dayVoting'; dayNumber: number }
  | { type: 'gameOver'; result: MafiaGameResult };

/** Simple string union of phase type names */
export type GamePhaseType =
  | 'lobby'
  | 'roleReveal'
  | 'night'
  | 'dayDiscussion'
  | 'dayVoting'
  | 'gameOver';

/** Phase data including timer and cycle info */
export interface PhaseData {
  phase: GamePhase;
  timerDuration: number;
  timerRemaining: number;
  cycleNumber: number;
}

// ============================================================================
// Game Configuration
// ============================================================================

/** Configurable game settings set by the host */
export interface MafiaGameConfig {
  discussionDuration: 30 | 60 | 90 | 120;
  votingDuration: 15 | 30 | 45;
  nightDuration: 15 | 30 | 45;
  extendedMode: boolean;
}

/** Default game configuration */
export const DEFAULT_MAFIA_CONFIG: MafiaGameConfig = {
  discussionDuration: 60,
  votingDuration: 30,
  nightDuration: 30,
  extendedMode: false,
};

// ============================================================================
// Night Action Types
// ============================================================================

/** State of all night actions for the current night */
export interface NightActionState {
  mafiaVotes: Map<string, string>;
  mafiaTarget: string | null;
  doctorProtections: Map<string, string>;
  detectiveInvestigations: Map<string, string>;
  investigationResults: Map<string, boolean>;
  lastProtectedPlayers: Map<string, string>;
}

/** Result of resolving a night phase */
export interface NightResolutionResult {
  mafiaTarget: string | null;
  wasProtected: boolean;
  eliminatedPlayerId: string | null;
  eliminatedPlayerName?: string;
  announcement: string;
}

/** Result of a detective investigation */
export interface InvestigationResult {
  targetId: string;
  targetName: string;
  isMafia: boolean;
}

// ============================================================================
// Voting Types
// ============================================================================

/** State of the day voting period */
export interface VotingState {
  votes: Map<string, string | null>;
  phase: 'active' | 'resolved';
}

/** Result of resolving a day vote */
export interface VotingResolutionResult {
  eliminatedPlayerId: string | null;
  eliminatedPlayerName?: string;
  eliminatedPlayerRole?: MafiaRole;
  voteTally: Map<string, number>;
  announcement: string;
  reason: 'majority' | 'no-majority' | 'tie';
}

// ============================================================================
// Chat Types
// ============================================================================

/** Chat channel: public day chat or private mafia night chat */
export type ChatChannel = 'day' | 'mafia-night';

/** A single chat message */
export interface ChatMessage {
  id: string;
  senderId: string;
  senderName: string;
  text: string;
  channel: ChatChannel;
  timestamp: number;
}

// ============================================================================
// Game Event Log Types
// ============================================================================

/** Types of game events logged in the event log */
export type GameEventType =
  | 'phase_transition'
  | 'night_elimination'
  | 'night_save'
  | 'day_elimination'
  | 'no_elimination'
  | 'vote_tie'
  | 'player_disconnected'
  | 'player_reconnected'
  | 'player_auto_eliminated'
  | 'game_start'
  | 'game_over';

/** A single game event entry */
export interface GameEvent {
  id: string;
  type: GameEventType;
  message: string;
  timestamp: number;
  cycleNumber: number;
  metadata?: Record<string, unknown>;
}

/** Event type → display color mapping */
export const EVENT_COLORS: Record<GameEventType, string> = {
  phase_transition: 'blue',
  night_elimination: 'red',
  night_save: 'green',
  day_elimination: 'red',
  no_elimination: 'yellow',
  vote_tie: 'yellow',
  player_disconnected: 'gray',
  player_reconnected: 'gray',
  player_auto_eliminated: 'red',
  game_start: 'blue',
  game_over: 'purple',
};

// ============================================================================
// Win Condition Types
// ============================================================================

/** Result of checking the win condition */
export interface WinConditionResult {
  gameOver: boolean;
  winner: Team | null;
  reason?: string;
}

/** Complete result of a finished game */
export interface MafiaGameResult {
  winner: Team;
  winReason: string;
  players: MafiaPlayerResult[];
  eventLog: GameEvent[];
  totalCycles: number;
  gameDuration: number;
}

/** Per-player result at game end */
export interface MafiaPlayerResult {
  id: string;
  name: string;
  role: MafiaRole;
  team: Team;
  survived: boolean;
  eliminatedCycle?: number;
  eliminatedPhase?: 'night' | 'day';
}

// ============================================================================
// Complete Game State
// ============================================================================

/** Full server-side game state */
export interface MafiaGameState {
  roomCode: string;
  config: MafiaGameConfig;
  players: MafiaPlayer[];
  hostId: string;
  phase: GamePhase;
  cycleNumber: number;
  phaseStartTime: number;
  nightAction: NightActionState;
  votingState: VotingState;
  dayChatMessages: ChatMessage[];
  mafiaChatMessages: ChatMessage[];
  eventLog: GameEvent[];
  gameStatus: MafiaGameStatus;
  roleAssignments: RoleAssignment[];
}

/** Discriminated union for overall game status */
export type MafiaGameStatus =
  | { type: 'lobby' }
  | { type: 'inProgress' }
  | { type: 'complete'; result: MafiaGameResult };

/** A single role assignment linking a player to their role */
export interface RoleAssignment {
  playerId: string;
  role: MafiaRole;
  team: Team;
}

// ============================================================================
// Player-Visible State (Client-Side)
// ============================================================================

/** State sent to a specific player — hides other players' roles and night actions */
export interface PlayerVisibleState {
  roomCode: string;
  config: MafiaGameConfig;
  players: VisiblePlayer[];
  hostId: string;
  phase: GamePhase;
  cycleNumber: number;
  phaseTimeRemaining: number;
  myRole: MafiaRole;
  myTeam: Team;
  mafiaMembers?: string[];
  mafiaVotes?: Map<string, string>;
  investigationResult?: InvestigationResult;
  lastProtectedPlayerId?: string;
  votes: Map<string, string | null>;
  dayChatMessages: ChatMessage[];
  mafiaChatMessages?: ChatMessage[];
  eventLog: GameEvent[];
  gameStatus: MafiaGameStatus;
}

/** Player info visible to other players (role hidden unless dead/game over) */
export interface VisiblePlayer {
  id: string;
  name: string;
  status: PlayerStatus;
  connectionStatus: ConnectionStatus;
  isHost: boolean;
  role?: MafiaRole;
}

// ============================================================================
// Action Types
// ============================================================================

/** Types of actions a player can perform */
export type MafiaActionType =
  | 'mafiaVote'
  | 'doctorProtect'
  | 'detectiveInvestigate'
  | 'dayVote'
  | 'chatMessage';

/** A player action submitted to the engine */
export interface MafiaAction {
  type: MafiaActionType;
  playerId: string;
  targetId?: string;
  message?: string;
  channel?: ChatChannel;
  timestamp: number;
}

/** Result of attempting an action */
export interface ActionResult {
  success: boolean;
  error?: string;
}

/** Result of a phase transition */
export interface PhaseTransitionResult {
  success: boolean;
  previousPhase: GamePhase;
  newPhase: GamePhase;
  error?: string;
}

// ============================================================================
// Statistics Types
// ============================================================================

/** Aggregate Mafia statistics for a player */
export interface MafiaStatistics {
  gamesPlayed: number;
  gamesWon: number;
  gamesWonAsVillage: number;
  gamesWonAsMafia: number;
  survivalRate: number;
  roleStats: Record<MafiaRole, RoleStatistics>;
}

/** Per-role statistics */
export interface RoleStatistics {
  timesAssigned: number;
  wins: number;
  winRate: number;
}

// ============================================================================
// Role Distribution Function
// ============================================================================

/**
 * Returns the role distribution for a given player count and mode.
 *
 * Standard Mode (5–15 players):
 *   5–6:  1 Mafia, 1 Doctor, 1 Detective, rest Villagers
 *   7–9:  2 Mafia, 1 Doctor, 1 Detective, rest Villagers
 *   10–12: 3 Mafia, 1 Doctor, 1 Detective, rest Villagers
 *   13–15: 4 Mafia, 1 Doctor, 1 Detective, rest Villagers
 *
 * Extended Mode (16–30 players):
 *   16–18: 5 Mafia, 2 Doctors, 2 Detectives, rest Villagers
 *   19–21: 6 Mafia, 2 Doctors, 2 Detectives, rest Villagers
 *   22–24: 7 Mafia, 2 Doctors, 3 Detectives, rest Villagers
 *   25–27: 8 Mafia, 3 Doctors, 3 Detectives, rest Villagers
 *   28–30: 9 Mafia, 3 Doctors, 3 Detectives, rest Villagers
 *
 * @param playerCount - Number of players (5–30)
 * @param extendedMode - Whether Extended Mode is enabled (allows 16–30 players)
 * @returns The role distribution for the given player count
 * @throws Error if playerCount is out of range for the selected mode
 */
export function getRoleDistribution(
  playerCount: number,
  extendedMode: boolean
): RoleDistribution {
  if (!Number.isInteger(playerCount)) {
    throw new Error(`Player count must be an integer, got ${playerCount}`);
  }

  if (extendedMode) {
    // Extended Mode: 5–30 players
    if (playerCount < 5 || playerCount > 30) {
      throw new Error(
        `Extended Mode requires 5–30 players, got ${playerCount}`
      );
    }
  } else {
    // Standard Mode: 5–15 players
    if (playerCount < 5 || playerCount > 15) {
      throw new Error(
        `Standard Mode requires 5–15 players, got ${playerCount}`
      );
    }
  }

  let base: Omit<RoleDistribution, 'villager'>;

  if (playerCount >= 28) {
    base = EXTENDED_DISTRIBUTION['28-30'];
  } else if (playerCount >= 25) {
    base = EXTENDED_DISTRIBUTION['25-27'];
  } else if (playerCount >= 22) {
    base = EXTENDED_DISTRIBUTION['22-24'];
  } else if (playerCount >= 19) {
    base = EXTENDED_DISTRIBUTION['19-21'];
  } else if (playerCount >= 16) {
    base = EXTENDED_DISTRIBUTION['16-18'];
  } else if (playerCount >= 13) {
    base = STANDARD_DISTRIBUTION['13-15'];
  } else if (playerCount >= 10) {
    base = STANDARD_DISTRIBUTION['10-12'];
  } else if (playerCount >= 7) {
    base = STANDARD_DISTRIBUTION['7-9'];
  } else {
    // 5–6
    base = STANDARD_DISTRIBUTION['5-6'];
  }

  return {
    mafia: base.mafia,
    doctor: base.doctor,
    detective: base.detective,
    villager: playerCount - base.mafia - base.doctor - base.detective,
  };
}
